import {
  Currency,
  CurrencyAmount,
  Pool,
  Position,
  Price,
  Token,
  ZERO,
  INITIAL_POOL_FEE,
  Bound,
  Field,
  Rounding,
  tryParseAmount,
  tryParseTick,
  tickToPrice,
  priceToClosestTick,
  nearestUsableTick,
  encodeSqrtRatioX96,
  TickMath,
  getTickToPrice
} from '@cryptoalgebra/kim-sdk';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { create } from 'zustand';
import { PoolState, PoolStateType, usePool } from 'src/hooks/v3/usePool';
import { PresetsType } from 'src/types/presets';
import { Address } from '@thirdweb-dev/sdk';
import { useAddress, useBalance, useContract } from '@thirdweb-dev/react';
import { StrategyData, StrategyDetails } from '@app/types/plugins';
import { useProviderStrategies } from '@app/hooks/plugins/useProviderStrategies';
import { useStrategyData } from '@app/hooks/plugins/useStrategyData';
import { useVaultApr, VaultApr } from '@app/hooks/plugins/useVaultApr';
import {
  useVaultDetailsByAddress,
  VaultDetails
} from '@app/hooks/plugins/useVaultDetailsByAddress';
import { useTokensRatios } from '@app/hooks/plugins/useTokensRatios';
import { ethers } from 'ethers';
import { trimDecimalsOverflow } from '@app/helpers/decimals';
import { useVaults, Vault } from '@app/hooks/plugins/useVaults';
import { useAsyncFn } from 'react-use';
import { EMPTY_ADDRESS } from '@app/constants/common';
import fromExponential from 'from-exponential';
import { useRouter } from 'next/router';
import { useIsNitroPool } from '@app/hooks/nitro/useIsNitroPool';
import { useConfig } from '@app/config';
import { getCurrencyFromToken } from '@app/helpers/currency';
import {
  AlgebraPoolNitroDetails,
  useAlgebraPoolNitroDetails
} from '@app/hooks/nitro/useAlgebraPoolNitroDetails';

export type FullRange = true;
export type Mode = 'manual' | 'auto';
export type StrategyProvider = 'steer' | 'ichi' | 'gamma';

interface MintState {
  readonly independentField: Field;
  readonly typedValue: string;
  readonly startPriceTypedValue: string; // for the case when there's no liquidity
  readonly leftRangeTypedValue: string | FullRange;
  readonly rightRangeTypedValue: string | FullRange;
  readonly dynamicFee: number;
  readonly preset: PresetsType | null;
  readonly txHash: string;
  readonly showNewestPosition: boolean;
  readonly initialUSDPrices: {
    [Field.CURRENCY_A]: string;
    [Field.CURRENCY_B]: string;
  };
  readonly initialTokenPrice: string;
  readonly currentStep: number;
  readonly mode: Mode;
  readonly provider: StrategyProvider | null;
  readonly strategy: StrategyDetails | null;
  readonly lockDuration: number;
  readonly stakeToNitro: boolean;
  readonly actions: {
    updateDynamicFee: (dynamicFee: number) => void;
    resetMintState: () => void;
    setFullRange: () => void;
    typeStartPriceInput: (typedValue: string) => void;
    typeLeftRangeInput: (typedValue: string) => void;
    typeRightRangeInput: (typedValue: string) => void;
    typeInput: (field: Field, typedValue: string, noLiquidity: boolean) => void;
    updateSelectedPreset: (preset: PresetsType | null) => void;
    setAddLiquidityTxHash: (txHash: string) => void;
    setInitialTokenPrice: (typedValue: string) => void;
    updateCurrentStep: (currentStep: number) => void;
    setMode: (nextMode: Mode) => void;
    setProvider: (provider: StrategyProvider | null) => void;
    setStrategy: (strategy: StrategyDetails | null) => void;
    setLockDuration: (val: number) => void;
    setStakeToNitro: (val: boolean) => void;
  };
}

export interface IDerivedMintInfo {
  pool?: Pool | null;
  poolState: PoolStateType;
  ticks: { [bound in Bound]?: number | undefined };
  price?: Price<Token, Token>;
  pricesAtTicks: {
    [bound in Bound]?: Price<Token, Token> | undefined;
  };
  currencies: { [field in Field]?: Currency };
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> };
  dependentField: Field;
  parsedAmounts: { [field in Field]?: CurrencyAmount<Currency> };
  position: Position | undefined;
  noLiquidity?: boolean;
  errorMessage?: string;
  errorCode?: number;
  invalidPool: boolean;
  outOfRange: boolean;
  invalidRange: boolean;
  depositADisabled: boolean;
  depositBDisabled: boolean;
  invertPrice: boolean;
  ticksAtLimit: { [bound in Bound]?: boolean | undefined };
  dynamicFee: number;
  lowerPrice: any;
  upperPrice: any;
  tickSpacing: number;
}

export interface IDerivedMintInfoWithProvider {
  pool: Pool | null;
  token0: Currency | undefined;
  token1: Currency | undefined;
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> };
  strategies: StrategyDetails[] | undefined;
  strategyData: StrategyData | null | undefined;
  vaultApr: VaultApr | null | undefined;
  vaultDetails: VaultDetails | undefined;
  parsedAmounts: { [field in Field]?: CurrencyAmount<Currency> };
  independentField: Field;
  dependentField: Field;
  vaultAddress: Address | undefined;
  isSwappedPair: boolean;
  lockDuration: number;
  selectedVault: Vault | undefined;
  errorMessage: string | undefined;
  nftPoolAddress: string | null | undefined;
  getPoolAddress: () => Promise<string | null | undefined>;
  isNitroPool: boolean;
  nitroDetails: AlgebraPoolNitroDetails | null;
}

const initialState = {
  independentField: Field.CURRENCY_A,
  typedValue: '',
  startPriceTypedValue: '',
  leftRangeTypedValue: '',
  rightRangeTypedValue: '',
  dynamicFee: 0,
  preset: null,
  txHash: '',
  showNewestPosition: false,
  initialUSDPrices: { [Field.CURRENCY_A]: '', [Field.CURRENCY_B]: '' },
  initialTokenPrice: '',
  currentStep: 0,
  mode: 'manual' as const,
  provider: 'steer' as const,
  strategy: null,
  lockDuration: 0,
  stakeToNitro: false
};

export const useMintState = create<MintState>((set, get) => ({
  ...initialState,
  actions: {
    updateDynamicFee: (dynamicFee: number) => set({ dynamicFee }),
    resetMintState: () => set(initialState),
    setFullRange: () =>
      set({ leftRangeTypedValue: true, rightRangeTypedValue: true }),
    typeStartPriceInput: (typedValue: string) =>
      set({ startPriceTypedValue: typedValue }),
    typeLeftRangeInput: (typedValue: string) =>
      set({ leftRangeTypedValue: typedValue }),
    typeRightRangeInput: (typedValue: string) =>
      set({ rightRangeTypedValue: typedValue }),
    typeInput: (field: Field, typedValue: string, noLiquidity: boolean) => {
      if (noLiquidity) {
        if (field === get().independentField) {
          set({ independentField: field, typedValue });
        } else {
          set({ independentField: field, typedValue });
        }
      } else {
        set({ independentField: field, typedValue });
      }
    },
    updateSelectedPreset: (preset: PresetsType | null) => set({ preset }),
    setAddLiquidityTxHash: (txHash: string) => set({ txHash }),
    setInitialTokenPrice: (typedValue: string) =>
      set({ initialTokenPrice: typedValue }),
    updateCurrentStep: (currentStep: number) => set({ currentStep }),
    setMode: (nextMode: Mode) => set({ mode: nextMode }),
    setProvider: (provider: StrategyProvider | null) => set({ provider }),
    setStrategy: (strategy: StrategyDetails | null) => set({ strategy }),
    setLockDuration: (val: number) => set({ lockDuration: val }),
    setStakeToNitro: (val: boolean) => set({ stakeToNitro: val })
  }
}));

export function useMintActionHandlers(noLiquidity: boolean | undefined): {
  onFieldAInput: (typedValue: string) => void;
  onFieldBInput: (typedValue: string) => void;
  onLeftRangeInput: (typedValue: string) => void;
  onRightRangeInput: (typedValue: string) => void;
  onStartPriceInput: (typedValue: string) => void;
} {
  const {
    actions: {
      typeInput,
      typeLeftRangeInput,
      typeRightRangeInput,
      typeStartPriceInput
    }
  } = useMintState();

  const onFieldAInput = useCallback(
    (typedValue: string) =>
      typeInput(Field.CURRENCY_A, typedValue, noLiquidity === true),
    [noLiquidity, typeInput]
  );

  const onFieldBInput = useCallback(
    (typedValue: string) =>
      typeInput(Field.CURRENCY_B, typedValue, noLiquidity === true),
    [noLiquidity, typeInput]
  );

  const onLeftRangeInput = useCallback(
    (typedValue: string) => typeLeftRangeInput(typedValue),
    [typeLeftRangeInput]
  );

  const onRightRangeInput = useCallback(
    (typedValue: string) => typeRightRangeInput(typedValue),
    [typeRightRangeInput]
  );

  const onStartPriceInput = useCallback(
    (typedValue: string) => typeStartPriceInput(typedValue),
    [typeStartPriceInput]
  );

  return {
    onFieldAInput,
    onFieldBInput,
    onLeftRangeInput,
    onRightRangeInput,
    onStartPriceInput
  };
}

export function useDerivedMintInfo(
  currencyA?: Currency,
  currencyB?: Currency,
  poolAddress?: Address,
  feeAmount?: typeof INITIAL_POOL_FEE,
  baseCurrency?: Currency,
  existingPosition?: Position
): IDerivedMintInfo {
  const account = useAddress();

  const {
    independentField,
    typedValue,
    leftRangeTypedValue,
    rightRangeTypedValue,
    startPriceTypedValue
  } = useMintState();

  const dependentField =
    independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A;

  // currencies
  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: currencyA,
      [Field.CURRENCY_B]: currencyB
    }),
    [currencyA, currencyB]
  );

  // formatted with tokens
  const [tokenA, tokenB, baseToken] = useMemo(
    () => [currencyA?.wrapped, currencyB?.wrapped, baseCurrency?.wrapped],
    [currencyA, currencyB, baseCurrency]
  );

  const [token0, token1] = useMemo(
    () =>
      tokenA && tokenB && !tokenA.equals(tokenB)
        ? tokenA.sortsBefore(tokenB)
          ? [tokenA, tokenB]
          : [tokenB, tokenA]
        : [undefined, undefined],
    [tokenA, tokenB]
  );

  const [addressA, addressB] = [
    getCurrencyFromToken(token0, currencyA, currencyB)?.isNative &&
    !existingPosition
      ? undefined
      : token0?.address || '',
    getCurrencyFromToken(token1, currencyA, currencyB)?.isNative &&
    !existingPosition
      ? undefined
      : token1?.address || ''
  ] as Address[];

  const { data: token0Balance } = useBalance(addressA);

  const { data: token1Balance } = useBalance(addressB);

  const currencyBalances: { [field in Field]?: CurrencyAmount<Currency> } = {
    [Field.CURRENCY_A]:
      token0 && token0Balance
        ? CurrencyAmount.fromRawAmount(token0, token0Balance.value.toString())
        : undefined,
    [Field.CURRENCY_B]:
      token1 && token1Balance
        ? CurrencyAmount.fromRawAmount(token1, token1Balance.value.toString())
        : undefined
  };

  const [poolState, pool] = usePool(poolAddress as Address);

  const noLiquidity = poolState === PoolState.NOT_EXISTS;

  const dynamicFee = pool ? pool.fee : 100;

  const tickSpacing = pool ? pool.tickSpacing : 60;

  // note to parse inputs in reverse
  const invertPrice = Boolean(baseToken && token0 && !baseToken.equals(token0));

  // always returns the price with 0 as base token
  const price: Price<Token, Token> | undefined = useMemo(() => {
    // if no liquidity use typed value
    if (noLiquidity) {
      const parsedQuoteAmount = tryParseAmount(
        startPriceTypedValue,
        invertPrice ? token0 : token1
      );

      if (parsedQuoteAmount && token0 && token1) {
        const baseAmount = tryParseAmount('1', invertPrice ? token1 : token0);
        const price =
          baseAmount && parsedQuoteAmount
            ? new Price(
                baseAmount.currency,
                parsedQuoteAmount.currency,
                baseAmount.quotient,
                parsedQuoteAmount.quotient
              )
            : undefined;

        return (invertPrice ? price?.invert() : price) ?? undefined;
      }

      return undefined;
    } else {
      // get the amount of quote currency
      return pool && token0 ? pool.priceOf(token0) : undefined;
    }
  }, [noLiquidity, startPriceTypedValue, invertPrice, token1, token0, pool]);

  // check for invalid price input (converts to invalid ratio)
  const invalidPrice = useMemo(() => {
    const sqrtRatioX96 = price
      ? encodeSqrtRatioX96(price.numerator, price.denominator)
      : undefined;

    return (
      price &&
      sqrtRatioX96 &&
      !(
        BigInt(sqrtRatioX96.toString()) >=
          BigInt(TickMath.MIN_SQRT_RATIO.toString()) &&
        BigInt(sqrtRatioX96.toString()) <
          BigInt(TickMath.MAX_SQRT_RATIO.toString())
      )
    );
  }, [price]);

  // used for ratio calculation when pool not initialized
  const mockPool = useMemo(() => {
    if (tokenA && tokenB && feeAmount && price && !invalidPrice) {
      const currentTick = priceToClosestTick(price);
      const currentSqrt = TickMath.getSqrtRatioAtTick(currentTick);

      return new Pool(
        tokenA,
        tokenB,
        feeAmount,
        currentSqrt,
        0,
        currentTick,
        60,
        []
      );
    } else {
      return undefined;
    }
  }, [feeAmount, invalidPrice, price, tokenA, tokenB]);

  // if pool exists use it, if not use the mock pool
  const poolForPosition: Pool | undefined = pool ?? mockPool;

  // lower and upper limits in the tick space
  const tickSpaceLimits: {
    [bound in Bound]: number | undefined;
  } = useMemo(
    () => ({
      [Bound.LOWER]: tickSpacing
        ? nearestUsableTick(TickMath.MIN_TICK, tickSpacing)
        : undefined,
      [Bound.UPPER]: tickSpacing
        ? nearestUsableTick(TickMath.MAX_TICK, tickSpacing)
        : undefined
    }),
    [tickSpacing]
  );

  // parse typed range values and determine closest ticks
  // lower should always be a smaller tick
  const ticks: {
    [key: string]: number | undefined;
  } = useMemo(() => {
    return {
      [Bound.LOWER]:
        typeof existingPosition?.tickLower === 'number'
          ? existingPosition.tickLower
          : (invertPrice && typeof rightRangeTypedValue === 'boolean') ||
            (!invertPrice && typeof leftRangeTypedValue === 'boolean')
          ? tickSpaceLimits[Bound.LOWER]
          : invertPrice
          ? tryParseTick(
              token1,
              token0,
              rightRangeTypedValue.toString(),
              tickSpacing
            )
          : tryParseTick(
              token0,
              token1,
              leftRangeTypedValue.toString(),
              tickSpacing
            ),
      [Bound.UPPER]:
        typeof existingPosition?.tickUpper === 'number'
          ? existingPosition.tickUpper
          : (!invertPrice && typeof rightRangeTypedValue === 'boolean') ||
            (invertPrice && typeof leftRangeTypedValue === 'boolean')
          ? tickSpaceLimits[Bound.UPPER]
          : invertPrice
          ? tryParseTick(
              token1,
              token0,
              leftRangeTypedValue.toString(),
              tickSpacing
            )
          : tryParseTick(
              token0,
              token1,
              rightRangeTypedValue.toString(),
              tickSpacing
            )
    };
  }, [
    existingPosition,
    invertPrice,
    leftRangeTypedValue,
    rightRangeTypedValue,
    token0,
    token1,
    tickSpaceLimits,
    tickSpacing
  ]);

  const { [Bound.LOWER]: tickLower, [Bound.UPPER]: tickUpper } = ticks || {};

  // specifies whether the lower and upper ticks is at the exteme bounds
  const ticksAtLimit = useMemo(
    () => ({
      [Bound.LOWER]: feeAmount && tickLower === tickSpaceLimits.LOWER,
      [Bound.UPPER]: feeAmount && tickUpper === tickSpaceLimits.UPPER
    }),
    [tickSpaceLimits, tickLower, tickUpper, feeAmount]
  );

  // mark invalid range
  const invalidRange = Boolean(
    typeof tickLower === 'number' &&
      typeof tickUpper === 'number' &&
      tickLower >= tickUpper
  );

  // always returns the price with 0 as base token
  const pricesAtTicks = useMemo(() => {
    return {
      [Bound.LOWER]: getTickToPrice(token0, token1, ticks[Bound.LOWER]),
      [Bound.UPPER]: getTickToPrice(token0, token1, ticks[Bound.UPPER])
    };
  }, [token0, token1, ticks]);
  const { [Bound.LOWER]: lowerPrice, [Bound.UPPER]: upperPrice } =
    pricesAtTicks;

  // liquidity range warning
  const outOfRange = Boolean(
    !invalidRange &&
      price &&
      lowerPrice &&
      upperPrice &&
      (price.lessThan(lowerPrice) || price.greaterThan(upperPrice))
  );

  const independentAmount: CurrencyAmount<Currency> | undefined =
    tryParseAmount(typedValue, currencies[independentField]);

  const dependentAmount: CurrencyAmount<Currency> | undefined = useMemo(() => {
    // we wrap the currencies just to get the price in terms of the other token
    const wrappedIndependentAmount = independentAmount?.wrapped;
    const dependentCurrency =
      dependentField === Field.CURRENCY_B ? currencyB : currencyA;

    if (
      independentAmount &&
      wrappedIndependentAmount &&
      typeof tickLower === 'number' &&
      typeof tickUpper === 'number' &&
      poolForPosition
    ) {
      // if price is out of range or invalid range - return 0 (single deposit will be independent)
      if (outOfRange || invalidRange) {
        return undefined;
      }

      const position: Position | undefined =
        wrappedIndependentAmount.currency.equals(poolForPosition.token0)
          ? Position.fromAmount0({
              pool: poolForPosition,
              tickLower,
              tickUpper,
              amount0: independentAmount.quotient,
              useFullPrecision: true // we want full precision for the theoretical position
            })
          : Position.fromAmount1({
              pool: poolForPosition,
              tickLower,
              tickUpper,
              amount1: independentAmount.quotient
            });

      const dependentTokenAmount = wrappedIndependentAmount.currency.equals(
        poolForPosition.token0
      )
        ? position.amount1
        : position.amount0;

      return (
        dependentCurrency &&
        CurrencyAmount.fromRawAmount(
          dependentCurrency,
          dependentTokenAmount.quotient
        )
      );
    }

    return undefined;
  }, [
    independentAmount,
    outOfRange,
    dependentField,
    currencyB,
    currencyA,
    tickLower,
    tickUpper,
    poolForPosition,
    invalidRange
  ]);

  const parsedAmounts: {
    [field in Field]: CurrencyAmount<Currency> | undefined;
  } = useMemo(() => {
    return {
      [Field.CURRENCY_A]:
        independentField === Field.CURRENCY_A
          ? independentAmount
          : dependentAmount,
      [Field.CURRENCY_B]:
        independentField === Field.CURRENCY_A
          ? dependentAmount
          : independentAmount
    };
  }, [dependentAmount, independentAmount, independentField]);

  // single deposit only if price is out of range
  const deposit0Disabled = Boolean(
    typeof tickUpper === 'number' &&
      poolForPosition &&
      poolForPosition.tickCurrent >= tickUpper
  );
  const deposit1Disabled = Boolean(
    typeof tickLower === 'number' &&
      poolForPosition &&
      poolForPosition.tickCurrent <= tickLower
  );

  // sorted for token order
  const depositADisabled =
    invalidRange ||
    Boolean(
      (deposit0Disabled &&
        poolForPosition &&
        tokenA &&
        poolForPosition.token0.equals(tokenA)) ||
        (deposit1Disabled &&
          poolForPosition &&
          tokenA &&
          poolForPosition.token1.equals(tokenA))
    );
  const depositBDisabled =
    invalidRange ||
    Boolean(
      (deposit0Disabled &&
        poolForPosition &&
        tokenB &&
        poolForPosition.token0.equals(tokenB)) ||
        (deposit1Disabled &&
          poolForPosition &&
          tokenB &&
          poolForPosition.token1.equals(tokenB))
    );

  // create position entity based on users selection
  const position: Position | undefined = useMemo(() => {
    if (
      !poolForPosition ||
      !tokenA ||
      !tokenB ||
      typeof tickLower !== 'number' ||
      typeof tickUpper !== 'number' ||
      invalidRange
    ) {
      return undefined;
    }

    // mark as 0 if disabled because out of range
    const amount0 = !deposit0Disabled
      ? parsedAmounts?.[
          tokenA.equals(poolForPosition.token0)
            ? Field.CURRENCY_A
            : Field.CURRENCY_B
        ]?.quotient
      : ZERO;
    const amount1 = !deposit1Disabled
      ? parsedAmounts?.[
          tokenA.equals(poolForPosition.token0)
            ? Field.CURRENCY_B
            : Field.CURRENCY_A
        ]?.quotient
      : ZERO;

    if (amount0 !== undefined && amount1 !== undefined) {
      return Position.fromAmounts({
        pool: poolForPosition,
        tickLower,
        tickUpper,
        amount0,
        amount1,
        useFullPrecision: true // we want full precision for the theoretical position
      });
    } else {
      return undefined;
    }
  }, [
    parsedAmounts,
    poolForPosition,
    tokenA,
    tokenB,
    deposit0Disabled,
    deposit1Disabled,
    invalidRange,
    tickLower,
    tickUpper
  ]);

  let errorMessage: string | undefined;
  let errorCode: number | undefined;

  if (!account) {
    errorMessage = `Connect Wallet`;
    errorCode = 0;
  }

  if (poolState === PoolState.INVALID) {
    errorMessage = errorMessage ?? `Invalid pair`;
    errorCode = errorCode ?? 1;
  }

  if (invalidPrice) {
    errorMessage = errorMessage ?? `Invalid price input`;
    errorCode = errorCode ?? 2;
  }

  if (
    (!parsedAmounts[Field.CURRENCY_A] && !depositADisabled) ||
    (!parsedAmounts[Field.CURRENCY_B] && !depositBDisabled)
  ) {
    errorMessage = errorMessage ?? `Enter an amount`;
    errorCode = errorCode ?? 3;
  }

  if (!currencyA || !currencyB) {
    errorMessage = 'Select a pair';
    errorCode = errorCode ?? 3;
  }

  const {
    [Field.CURRENCY_A]: currencyAAmount,
    [Field.CURRENCY_B]: currencyBAmount
  } = parsedAmounts;

  const balanceA =
    currencyBalances.CURRENCY_A?.currency.wrapped.symbol ===
    currencyA?.wrapped.symbol
      ? currencyBalances.CURRENCY_A
      : currencyBalances.CURRENCY_B;

  if (currencyAAmount && balanceA?.lessThan(currencyAAmount)) {
    errorMessage = `Insufficient ${currencyA?.symbol} balance`;
    errorCode = errorCode ?? 4;
  }

  const balanceB =
    currencyBalances.CURRENCY_B?.currency.wrapped.address ===
    currencyB?.wrapped.address
      ? currencyBalances.CURRENCY_B
      : currencyBalances.CURRENCY_A;

  if (currencyBAmount && balanceB?.lessThan(currencyBAmount)) {
    errorMessage = `Insufficient ${currencyB?.symbol} balance`;
    errorCode = errorCode ?? 5;
  }

  const invalidPool = poolState === PoolState.INVALID;

  return {
    dependentField,
    currencies,
    pool,
    poolState,
    currencyBalances,
    parsedAmounts,
    ticks,
    price,
    pricesAtTicks,
    position,
    noLiquidity,
    errorMessage,
    errorCode,
    invalidPool,
    invalidRange,
    outOfRange,
    depositADisabled,
    depositBDisabled,
    invertPrice,
    ticksAtLimit,
    dynamicFee,
    lowerPrice,
    upperPrice,
    tickSpacing
  };
}

export function useRangeHopCallbacks(
  baseCurrency: Currency | undefined,
  quoteCurrency: Currency | undefined,
  tickSpacing: number,
  tickLower: number | undefined,
  tickUpper: number | undefined,
  pool?: Pool | undefined | null
) {
  const {
    actions: { setFullRange }
  } = useMintState();

  const baseToken = useMemo(() => baseCurrency?.wrapped, [baseCurrency]);
  const quoteToken = useMemo(() => quoteCurrency?.wrapped, [quoteCurrency]);

  const getDecrementLower = useCallback(
    (rate = 1) => {
      if (
        baseToken &&
        quoteToken &&
        typeof tickLower === 'number' &&
        tickSpacing
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          tickLower - tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }
      // use pool current tick as starting tick if we have pool but no tick input

      if (
        !(typeof tickLower === 'number') &&
        baseToken &&
        quoteToken &&
        tickSpacing &&
        pool
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          pool.tickCurrent - tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      return '';
    },
    [baseToken, quoteToken, tickLower, tickSpacing, pool]
  );

  const getIncrementLower = useCallback(
    (rate = 1) => {
      if (
        baseToken &&
        quoteToken &&
        typeof tickLower === 'number' &&
        tickSpacing
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          tickLower + tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      // use pool current tick as starting tick if we have pool but no tick input
      if (
        !(typeof tickLower === 'number') &&
        baseToken &&
        quoteToken &&
        tickSpacing &&
        pool
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          pool.tickCurrent + tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      return '';
    },
    [baseToken, quoteToken, tickLower, tickSpacing, pool]
  );

  const getDecrementUpper = useCallback(
    (rate = 1) => {
      if (
        baseToken &&
        quoteToken &&
        typeof tickUpper === 'number' &&
        tickSpacing
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          tickUpper - tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      // use pool current tick as starting tick if we have pool but no tick input
      if (
        !(typeof tickUpper === 'number') &&
        baseToken &&
        quoteToken &&
        tickSpacing &&
        pool
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          pool.tickCurrent - tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      return '';
    },
    [baseToken, quoteToken, tickUpper, tickSpacing, pool]
  );

  const getIncrementUpper = useCallback(
    (rate = 1) => {
      if (
        baseToken &&
        quoteToken &&
        typeof tickUpper === 'number' &&
        tickSpacing
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          tickUpper + tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      // use pool current tick as starting tick if we have pool but no tick input
      if (
        !(typeof tickUpper === 'number') &&
        baseToken &&
        quoteToken &&
        tickSpacing &&
        pool
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          pool.tickCurrent + tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      return '';
    },
    [baseToken, quoteToken, tickUpper, tickSpacing, pool]
  );

  const getSetRange = useCallback(
    (numTicks: number) => {
      if (baseToken && quoteToken && tickSpacing && pool) {
        // calculate range around current price given `numTicks`
        const newPriceLower = tickToPrice(
          baseToken,
          quoteToken,
          Math.max(TickMath.MIN_TICK, pool.tickCurrent - numTicks)
        );
        const newPriceUpper = tickToPrice(
          baseToken,
          quoteToken,
          Math.min(TickMath.MAX_TICK, pool.tickCurrent + numTicks)
        );

        return [
          newPriceLower.toSignificant(5, undefined, Rounding.ROUND_UP),
          newPriceUpper.toSignificant(5, undefined, Rounding.ROUND_UP)
        ];
      }

      return ['', ''];
    },
    [baseToken, quoteToken, tickSpacing, pool]
  );

  const getSetFullRange = useCallback(() => setFullRange(), [setFullRange]);

  return {
    getDecrementLower,
    getIncrementLower,
    getDecrementUpper,
    getIncrementUpper,
    getSetRange,
    getSetFullRange,
    setFullRange
  };
}

export function useDerivedLiquidityInfo(
  currencyA?: Currency,
  currencyB?: Currency,
  poolAddress?: Address,
  baseCurrency?: Currency
): IDerivedMintInfoWithProvider {
  const { query } = useRouter();
  const {
    strategy,
    typedValue,
    independentField,
    lockDuration,
    mode,
    actions: { setStrategy, setMode }
  } = useMintState();
  const dependentField =
    independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A;
  const { strategies } = useProviderStrategies(poolAddress);
  const { data: strategyData } = useStrategyData(strategy?.strategyIpfsHash);
  const { data: vaultApr } = useVaultApr(strategy?.vaultAddress);
  const { data: vaultDetails } = useVaultDetailsByAddress(
    strategy?.vaultAddress
  );
  const { data: vaults } = useVaults();
  const config = useConfig();

  const { contract: poolFactoryContract } = useContract(
    config?.CONTRACTS.NFT_POOL_FACTORY
  );

  const [{ value: nftPoolAddress }, getPoolAddress] = useAsyncFn(async () => {
    if (!poolFactoryContract || !strategy) {
      return;
    }

    const nftPoolAddress = await poolFactoryContract.call('getPool', [
      strategy?.vaultAddress
    ]);

    return nftPoolAddress === EMPTY_ADDRESS ? null : nftPoolAddress;
  }, [poolFactoryContract, strategy]);

  const nitroDetails = useAlgebraPoolNitroDetails(nftPoolAddress);

  const { isNitroPool } = useIsNitroPool(nftPoolAddress);

  useEffect(() => {
    getPoolAddress();
  }, [getPoolAddress]);

  useEffect(() => {
    if (strategies && strategies.length && !strategy) {
      // set first available strategy as default selection
      setStrategy(strategies[0]);
    }
  }, [setStrategy, strategies, strategy]);

  const vaultSetRef = useRef(false);

  useEffect(() => {
    if (
      query &&
      query.vaultId &&
      mode === 'manual' &&
      strategies &&
      !vaultSetRef.current
    ) {
      const { vaultId } = query;
      const str = strategies?.find(s => s.vaultAddress === vaultId);

      if (str) {
        setMode('auto');
        setStrategy(str);
        vaultSetRef.current = true;
      }
    }
  }, [query.vaultId, vaults, strategies, mode, query, setMode, setStrategy]);

  const { data: tokensRatio } = useTokensRatios(
    poolAddress,
    currencyA,
    currencyB
  );

  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: currencyA,
      [Field.CURRENCY_B]: currencyB
    }),
    [currencyA, currencyB]
  );

  // formatted with tokens
  const [token0, token1] = useMemo(
    () => [currencyA, currencyB, baseCurrency],
    [currencyA, currencyB, baseCurrency]
  );

  const { data: token0Balance } = useBalance(
    token0?.isNative ? '' : token0?.wrapped.address
  );
  const { data: token1Balance } = useBalance(
    token1?.isNative ? '' : token1?.wrapped.address
  );
  const currencyBalances: { [field in Field]?: CurrencyAmount<Currency> } = {
    [Field.CURRENCY_A]:
      token0 && token0Balance
        ? CurrencyAmount.fromRawAmount(token0, token0Balance.value.toString())
        : undefined,
    [Field.CURRENCY_B]:
      token1 && token1Balance
        ? CurrencyAmount.fromRawAmount(token1, token1Balance.value.toString())
        : undefined
  };

  const [, pool] = usePool(poolAddress as Address);

  const independentAmount: CurrencyAmount<Currency> | undefined =
    tryParseAmount(typedValue, currencies[independentField]);

  const dependentAmount = useMemo(() => {
    if (!independentAmount || !tokensRatio || !currencies[dependentField]) {
      return;
    }

    const v = +independentAmount.toExact() / +tokensRatio.ratio;

    const amount = ethers.utils.parseUnits(
      trimDecimalsOverflow(
        fromExponential(v.toString()),
        currencies[dependentField]!.decimals
      ),
      currencies[dependentField]!.decimals
    );

    return currencies[dependentField]
      ? CurrencyAmount.fromRawAmount(
          currencies[dependentField]!,
          amount.toString()
        )
      : undefined;
  }, [independentAmount, tokensRatio, currencies, dependentField]);

  const parsedAmounts: {
    [field in Field]: CurrencyAmount<Currency> | undefined;
  } = useMemo(() => {
    return {
      [Field.CURRENCY_A]:
        independentField === Field.CURRENCY_A
          ? independentAmount
          : dependentAmount,
      [Field.CURRENCY_B]:
        independentField === Field.CURRENCY_A
          ? dependentAmount
          : independentAmount
    };
  }, [dependentAmount, independentAmount, independentField]);

  const isSwappedPair = useMemo(() => {
    if (!pool) {
      return false;
    }

    return (
      pool.token0.address.toLowerCase() !==
      currencyA?.wrapped.address.toLowerCase()
    );
  }, [pool, currencyA]);

  const selectedVault = useMemo(() => {
    if (!vaults) {
      return;
    }

    return vaults.find(
      item =>
        item.pool.toLowerCase() === poolAddress?.toLowerCase() &&
        item.name === strategy?.strategyName
    );
  }, [vaults, poolAddress, strategy?.strategyName]);

  let errorMessage;

  if (
    parsedAmounts.CURRENCY_A &&
    currencyBalances.CURRENCY_A?.lessThan(parsedAmounts.CURRENCY_A)
  ) {
    errorMessage = `Insufficient ${currencyA?.symbol} balance`;
  }

  if (
    parsedAmounts.CURRENCY_B &&
    currencyBalances.CURRENCY_B?.lessThan(parsedAmounts.CURRENCY_B)
  ) {
    errorMessage = `Insufficient ${currencyB?.symbol} balance`;
  }

  return {
    pool,
    token0,
    token1,
    currencyBalances,
    strategies,
    strategyData,
    vaultApr,
    vaultDetails,
    parsedAmounts,
    independentField,
    dependentField,
    vaultAddress: strategy?.vaultAddress,
    isSwappedPair,
    lockDuration,
    selectedVault,
    errorMessage,
    nftPoolAddress,
    getPoolAddress,
    isNitroPool,
    nitroDetails
  };
}
